import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Trash2, ShieldAlert, Palette, Save } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";

const SWATCHES = ["#0A0A0A", "#1E3A8A", "#1E40AF", "#064E3B", "#7C2D12", "#7F1D1D", "#312E81", "#831843"];

export default function BrokerageSettingsPage() {
  const { theme, brokerageName, isAdmin, refresh } = useTheme();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    display_name: "",
    primary_color: "#0A0A0A",
    accent_color: "#064E3B",
    footer_text: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm({
      display_name: theme?.display_name || "",
      primary_color: theme?.primary_color || "#0A0A0A",
      accent_color: theme?.accent_color || "#064E3B",
      footer_text: theme?.footer_text || "",
    });
  }, [theme]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/brokerage/theme", form);
      await refresh();
      toast.success("Brokerage theme updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 256 * 1024) {
      toast.error(`File too large (${Math.round(file.size / 1024)} KB). Max is 256 KB.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/brokerage/theme/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove the uploaded logo and fall back to the default?")) return;
    try {
      await api.delete("/brokerage/theme/logo");
      await refresh();
      toast.success("Logo cleared");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!isAdmin) {
    return (
      <div className="px-6 lg:px-10 py-8" data-testid="brokerage-settings-readonly">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Settings</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Brokerage Theme</h1>
        </div>
        <div className="border border-neutral-200 p-8 max-w-2xl">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-neutral-500 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-xl mb-1">Read-only</div>
              <p className="text-sm text-neutral-600 mb-4">
                Only the brokerage admin can edit the theme. Below is the current applied branding for <span className="font-semibold">{brokerageName}</span>.
              </p>
              <ReadOnlyPreview theme={theme} brokerageName={brokerageName} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="brokerage-settings-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Admin · Settings</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Brokerage Theme</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl">
          You&apos;re the admin of <span className="font-semibold text-neutral-900">{brokerageName}</span>. Changes here apply to every agent on your brokerage — across dashboards, tax reports, and CPA share links.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <form onSubmit={save} className="lg:col-span-2 border border-neutral-200 p-6 space-y-6">
          {/* Logo */}
          <section data-testid="theme-logo-section">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Logo</div>
            <div className="flex items-center gap-6">
              <div className="h-24 w-24 border border-neutral-300 flex items-center justify-center bg-white">
                {theme?.logo_data_url ? (
                  <img src={theme.logo_data_url} alt="Current logo" className="max-h-20 max-w-20" data-testid="current-logo-image" />
                ) : (
                  <CoreLogo variant="mark" size={64} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-neutral-600 mb-3">PNG, JPG, SVG, WebP, or GIF · max 256 KB. Recommend a transparent square ~512×512.</p>
                <div className="flex gap-2">
                  <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="upload-logo-button">
                    <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    {uploading ? "Uploading…" : "Upload logo"}
                  </Button>
                  {theme?.logo_data_url && (
                    <Button type="button" onClick={clearLogo} variant="outline" className="rounded-none border-neutral-300 hover:bg-neutral-100" data-testid="clear-logo-button">
                      <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Remove
                    </Button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
                </div>
              </div>
            </div>
          </section>

          {/* Brand name override */}
          <section>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Display Name</Label>
            <Input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder={brokerageName}
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
              data-testid="theme-display-name-input"
            />
            <p className="text-xs text-neutral-500 mt-1">Leave blank to use the legal brokerage name on file.</p>
          </section>

          {/* Colors */}
          <section>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3 flex items-center gap-1.5">
              <Palette className="h-3 w-3" strokeWidth={1.5} /> Brand Colors
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ColorField
                label="Primary"
                value={form.primary_color}
                onChange={(v) => setForm({ ...form, primary_color: v })}
                testId="primary-color"
              />
              <ColorField
                label="Accent (profit / highlight)"
                value={form.accent_color}
                onChange={(v) => setForm({ ...form, accent_color: v })}
                testId="accent-color"
              />
            </div>
          </section>

          {/* Footer text */}
          <section>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Footer Text</Label>
            <Textarea
              value={form.footer_text}
              onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
              placeholder="e.g. Coastal Realty Group · 100 Ocean Blvd, Newport · Lic CR-2018"
              className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 min-h-[72px]"
              maxLength={300}
              data-testid="theme-footer-input"
            />
            <p className="text-xs text-neutral-500 mt-1">Appears on tax reports, PDF/Excel/CSV exports, CPA share links, and the sidebar tip area. Max 300 characters.</p>
          </section>

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <Button type="submit" disabled={saving} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="theme-save-button">
              <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>

        {/* Live Preview */}
        <aside className="border border-neutral-200 p-6 self-start" data-testid="theme-preview">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Live Preview</div>
          <div className="border border-neutral-200 p-4 bg-white">
            <div className="flex items-center gap-3 pb-3 border-b border-neutral-200" style={{ color: form.primary_color }}>
              {theme?.logo_data_url ? (
                <img src={theme.logo_data_url} alt="" className="h-9 w-auto max-w-12" />
              ) : (
                <CoreLogo variant="mark" size={36} />
              )}
              <div>
                <div className="font-serif text-base leading-none" style={{ color: form.primary_color }}>{form.display_name || brokerageName}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-400 mt-1">Ledger / by brokerage</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Net Profit</div>
              <div className="font-mono text-2xl mt-1" style={{ color: form.accent_color }}>$25,716.00</div>
            </div>
            {form.footer_text && (
              <div className="mt-4 pt-3 border-t border-neutral-200 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400 leading-relaxed" data-testid="preview-footer-text">
                {form.footer_text}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</Label>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 border border-neutral-300 cursor-pointer"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
          data-testid={`${testId}-input`}
        />
      </div>
      <div className="mt-2 flex gap-1.5 flex-wrap">
        {SWATCHES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={`h-5 w-5 border ${value.toLowerCase() === c.toLowerCase() ? "border-neutral-900 ring-1 ring-neutral-900" : "border-neutral-300"}`}
            style={{ background: c }}
            aria-label={c}
            data-testid={`${testId}-swatch-${c.replace("#", "")}`}
          />
        ))}
      </div>
    </div>
  );
}

function ReadOnlyPreview({ theme, brokerageName }) {
  return (
    <div className="border border-neutral-200 p-4 bg-neutral-50 text-sm" data-testid="readonly-preview">
      <div className="flex items-center gap-3 mb-3">
        {theme?.logo_data_url ? (
          <img src={theme.logo_data_url} alt="" className="h-10 w-auto max-w-14" />
        ) : (
          <CoreLogo variant="mark" size={40} />
        )}
        <div className="font-serif text-lg">{theme?.display_name || brokerageName}</div>
      </div>
      <dl className="grid grid-cols-2 gap-y-1 text-xs">
        <dt className="text-neutral-500">Primary</dt>
        <dd className="font-mono flex items-center gap-1"><span className="inline-block h-3 w-3 border border-neutral-300" style={{ background: theme?.primary_color }} />{theme?.primary_color}</dd>
        <dt className="text-neutral-500">Accent</dt>
        <dd className="font-mono flex items-center gap-1"><span className="inline-block h-3 w-3 border border-neutral-300" style={{ background: theme?.accent_color }} />{theme?.accent_color}</dd>
        {theme?.footer_text && (
          <>
            <dt className="text-neutral-500 col-span-2 mt-2">Footer</dt>
            <dd className="col-span-2 italic text-neutral-700">{theme.footer_text}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
